import { __awaiter } from "tslib";
import { useCallback, useEffect, useRef, useState } from 'react';
import { SCROLL_LOAD_THRESHOLD } from './dropdown.constants';
/**
 * Infinite scrolling for a level of dropdown options.
 *
 * Pages are requested from two places: the scroll handler, once the user reaches the end of the list, and a layout
 * check that runs whenever the rendered options are too short to scroll. The second one is what loads the first page
 * of an async branch, so consumers only need `hasMore` to be true for the request to happen.
 */
export const useDropdownLoadMore = ({ hasMore = false, isLoading = false, itemCount, onLoadMore, }) => {
    const [scrollElement, setScrollElement] = useState(null);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const isRequestPendingRef = useRef(false);
    const canLoadMore = Boolean(onLoadMore) && hasMore && !isLoading;
    const loadMore = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!onLoadMore || isRequestPendingRef.current) {
            return;
        }
        isRequestPendingRef.current = true;
        setIsLoadingMore(true);
        try {
            yield onLoadMore();
        }
        finally {
            isRequestPendingRef.current = false;
            setIsLoadingMore(false);
        }
    }), [onLoadMore]);
    const handleScroll = useCallback((event) => {
        if (!canLoadMore || isRequestPendingRef.current) {
            return;
        }
        const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
        if (scrollHeight - scrollTop <= clientHeight + SCROLL_LOAD_THRESHOLD) {
            loadMore();
        }
    }, [canLoadMore, loadMore]);
    useEffect(() => {
        if (!canLoadMore || isLoadingMore || !scrollElement) {
            return;
        }
        const frameId = requestAnimationFrame(() => {
            // A collapsed container has not been laid out yet, so its scroll height says nothing about the list.
            if (scrollElement.clientHeight === 0) {
                return;
            }
            if (scrollElement.scrollHeight <= scrollElement.clientHeight + SCROLL_LOAD_THRESHOLD) {
                loadMore();
            }
        });
        return () => cancelAnimationFrame(frameId);
    }, [canLoadMore, isLoadingMore, itemCount, loadMore, scrollElement]);
    return { handleScroll, isLoadingMore, setScrollElement };
};
//# sourceMappingURL=use_dropdown_load_more.hook.js.map